import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Image, Typography, Row, Col, Card } from 'antd';
import generateKey from 'utils/generateKey';
import { useInterval } from 'hooks/useInterval';
import type { InputRef } from 'antd';

import mobileIcon from 'assets/images/neutral-1.svg';
import emailIcon from 'assets/images/Group 3.svg';
import styled from 'styled-components';
import { getConfig } from 'configs';
import { useRequestOtp, useEmailOtp, useEmailOtpValidate, useValidSecEmail } from 'all-in-one-shared-api';
import './otp.less';
import { useKeycloak } from 'all-in-one-react-keycloak';
import { setErrorCodeFun } from 'utils/setErrorCodeFun/setErrorCodeFun';
import { InvalidTextMsg, ResendCaptchaSection, SubTitle } from 'pages/registration/otp-verification/styledComponents';
import { NewInput } from 'pages/life/registration/otp-verification/styledComponents';
import GoogleCaptcha from 'components/googleCaptcha';
const { Title, Text, Link } = Typography;

const RESEND_COUNTDOWN_PERIOD = 60;

const config = getConfig(process.env.REACT_APP_ENVIRONMENT ?? 'mock');

interface LocationState {
  loginId: string;
  mobile?: string;
  countryCode?: string;
  email?: string;
  secEmail: string;
}

const ResponsiveContainer = styled.div`
  padding-left: 40px;
  padding-right: 40px;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;
export const PrimaryOtpVerificationPage = (): JSX.Element => {
  const [otpArray, setOtpArray] = useState('');
  const [isAllowEdit, setIsAllowEdit] = useState(false);
  const { keycloak } = useKeycloak();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN_PERIOD);
  const [requestOtpCount, setRequestOtpCount] = useState(0);
  const [isShowCaptcha, setIsShowCaptcha] = useState(false);
  const location = useLocation();
  const { loginId, mobile, email, countryCode, secEmail } = (location.state as LocationState) ?? {};

  const mobileNumber = (countryCode ?? '') + (mobile ?? '');
  const [key, setKey] = useState<string>('');
  const [randomsV, setMathRandomV] = useState(Math.random());

  const { shouldFetch: validSecEmailFetch } = useValidSecEmail(
    'useValidSecEmail' + randomsV,
    keycloak.token as string,
    secEmail,
    {
      onSuccess: () => {
        validSecEmailFetch(false);
      },
      onError: (error) => {
        validSecEmailFetch(false);
        setErrorMsg(error.code);
      },
    },
  );
  const { shouldFetch } = useRequestOtp(key, loginId ?? '', mobileNumber, {
    shouldRetryOnError: false,
    onSuccess: () => {
      shouldFetch(false);
    },
    onError: (error) => {
      if (error) {
        shouldFetch(false);
        const errorMessage = setErrorCodeFun(error.code, i18n.language);
        setErrorMsg(errorMessage ?? error.code ?? error.message);
      }
    },
  });
  const { shouldFetch: shouldEmailFetch } = useEmailOtp(key, loginId ?? '', email ?? '', {
    shouldRetryOnError: false,
    onSuccess: () => {
      shouldEmailFetch(false);
    },
    onError: (error) => {
      if (error) {
        shouldEmailFetch(false);
        const errorMessage = setErrorCodeFun(error.code, i18n.language);
        setErrorMsg(errorMessage ?? error.code ?? error.message);
      }
    },
  });

  const [validateKey, setValidateKey] = useState<string>('');
  const { shouldFetch: shouldEmailValidate } = useEmailOtpValidate(validateKey, loginId ?? '', otpArray, {
    shouldRetryOnError: false,
    onSuccess: (validateResponse) => {
      shouldEmailValidate(false);
      if (validateResponse) {
        if (validateResponse.success) {
          setErrorMsg(null);
          startUsing();
        } else {
          //光標移入第一個輸入框
          if (digit1InputRef.current) {
            digit1InputRef.current.focus();
          } else {
            console.error('digit1InputRef.current is null');
          }
          setOtpArray('');
          setErrorMsg(validateResponse.message);
        }
      }
    },
    onError: (error) => {
      if (error) {
        shouldEmailValidate(false);
        const errorMessage = setErrorCodeFun(error.code, i18n.language);
        setErrorMsg(errorMessage ?? error.code ?? error.message);
      }
    },
  });

  useInterval(
    () => {
      setCountdown(countdown - 1);
    },
    countdown >= 0 ? 1000 : null,
  );
  const digit1InputRef = useRef<InputRef>(null);

  const handleResendOtp = () => {
    if (requestOtpCount < 3) {
      setCountdown(RESEND_COUNTDOWN_PERIOD);
    }
    setRequestOtpCount(requestOtpCount + 1);
    console.log('onPressResend()->');
    setKey(generateKey());

    setOtpArray('');
    setErrorMsg('life.register.resendOtp');
    if (digit1InputRef.current) {
      digit1InputRef.current.focus();
    }
    setMathRandomV(Math.random());
    validSecEmailFetch(true);
  };
  useEffect(() => {
    if (requestOtpCount >= 3) {
      setIsShowCaptcha(true);
    }
  }, [requestOtpCount, setIsShowCaptcha]);
  const startUsing = async () => {
    // AddLifePolicyFetch(true);
    // 使用 replace 代替 reset，来模拟重置历史记录
    // navigate('/addConfirmEb', { replace: true });
    navigate('/secOtpVerificationPage', {
      state: {
        ebOtpCode: otpArray,
        secEmail,
        email: email,
        mobile: mobile,
        countryCode: countryCode,
        loginId,
      },
    });
  };
  /* eslint-disable */
  // useEffect(() => {
  //   if (AddLifePolicyData) {
  // refreshToken(AddLifePolicyData.lifeInsuranceList);
  //   }
  // }, [AddLifePolicyData, AddLifePolicyError]);
  /* eslint-enable */

  const handleVerifyOtp = useCallback(() => {
    if (!/\d{6}/.test(otpArray)) {
      setErrorMsg(t('smsOtpVerification.code-not-found'));
      return;
    }

    setValidateKey(generateKey());
    shouldEmailValidate(true);
  }, [otpArray, setErrorMsg, setValidateKey, shouldEmailValidate, t]);
  useEffect(() => {
    if (otpArray.length === 6) {
      handleVerifyOtp();
    }
  }, [otpArray, handleVerifyOtp]);
  useEffect(() => {
    if (errorMsg && errorMsg == 'smsOtpVerification.retry-limit-exceeded') {
      setIsAllowEdit(true);

      setCountdown(RESEND_COUNTDOWN_PERIOD);
    }
  }, [errorMsg]);
  useEffect(() => {
    if (countdown === 0) {
      setIsAllowEdit(false);
    }
  }, [countdown]);

  const onCaptchaSuccess = () => {
    setIsShowCaptcha(false);
  };

  const onCaptchaError = () => {
    setErrorMsg(t('login.error.captchaDigitError'));
  };
  const onOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) {
      return;
    }
    setOtpArray(e.target.value);
    setErrorMsg(null);
  };
  function maskEmail(email: string) {
    if (!email) return '';
    const [username, domain] = email.split('@');
    const domainList = domain.split('.');

    const maskedUsername = username.charAt(0) + '*'.repeat(username.length - 2) + username.slice(-1);
    const maskedDomain = domainList[0].charAt(0) + '*'.repeat(domainList[0].length - 2) + domainList[0].slice(-1);
    if (domainList.length > 2) {
      return maskedUsername + '@' + maskedDomain + '.' + domainList[1] + '.' + domainList[2];
    } else {
      return maskedUsername + '@' + maskedDomain + '.' + domainList[1];
    }
  }
  function maskPhoneNumber(phoneNumber: string) {
    if (!phoneNumber) return '';
    const visibleDigits = 2;
    const startDigits = phoneNumber.substring(0, visibleDigits);
    const endDigits = phoneNumber.substring(phoneNumber.length - 2);

    const maskedPart = '*'.repeat(phoneNumber.length - visibleDigits - 2);

    return startDigits + maskedPart + endDigits;
  }
  return (
    <ResponsiveContainer>
      <Typography.Title
        level={3}
        style={{ fontSize: '33px', fontFamily: 'Roboto400', color: '#20252b', fontWeight: '400' }}
      >
        {t('profile.otpTitle')}
      </Typography.Title>
      <div
        className="register_otp_wrapper"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          backgroundColor: 'white',
          padding: '20px',
        }}
      >
        <Card style={{ border: 0, width: '100%', maxWidth: '400px' }}>
          <Row justify="center" gutter={[16, { xs: 8, sm: 16, md: 24 }]}>
            <Col xs={24} md={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <Title level={3} style={{ marginTop: '30px', fontSize: '26px', color: '#20252B' }}>
                {t('smsOtpVerification.screenTitle')}
              </Title>
            </Col>
            <Col xs={24} md={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <Image height="176px" className="img_container" src={email ? emailIcon : mobileIcon} preview={false} />
            </Col>
            <Col xs={24} md={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <SubTitle
                style={{ display: 'inline-block', textAlign: 'center', fontSize: '20px', fontFamily: 'Roboto400' }}
              >
                {email ? t('smsOtpVerification.emailScreenSubtitle') : t('smsOtpVerification.mobileScreenSubtitle')}
                <br />
                {email ? maskEmail(email) : mobile ? countryCode + '  ' + maskPhoneNumber(mobile) : null}
              </SubTitle>
            </Col>

            <Col xs={24} md={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <NewInput
                maxLength={6}
                value={otpArray}
                onChange={onOtpChange}
                className="single-digit-input"
                autoFocus
                ref={digit1InputRef}
                disabled={isAllowEdit}
              />
            </Col>
            <Col xs={24} md={24} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {errorMsg && (
                <InvalidTextMsg type="danger" style={{ marginLeft: 0 }}>
                  {t(errorMsg)}
                </InvalidTextMsg>
              )}
            </Col>

            <Col xs={24} md={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <ResendCaptchaSection>
                {isShowCaptcha ? (
                  <GoogleCaptcha
                    hl={i18n.language}
                    sitekey={config.captchaSiteKey}
                    onChange={onCaptchaSuccess}
                    onError={onCaptchaError}
                    onExpired={onCaptchaError}
                  />
                ) : (
                  <>
                    <Link
                      onClick={countdown > 0 ? (e) => e.preventDefault() : handleResendOtp}
                      style={{
                        fontSize: '17px',
                        fontFamily: 'Roboto400',
                        pointerEvents: countdown > 0 ? 'none' : 'auto',
                        color: countdown > 0 ? 'gray' : '#752127', // 设置禁用时颜色为灰色
                      }}
                    >
                      {t('smsOtpVerification.resend')}
                    </Link>
                    {countdown >= 0 && (
                      <Text type="secondary" style={{ color: '#20252B', fontSize: '17px', marginTop: 20 }}>
                        {t('smsOtpVerification.resendCountdownText', {
                          second: countdown,
                        })}
                      </Text>
                    )}
                  </>
                )}
              </ResendCaptchaSection>
            </Col>

            <Link
              onClick={() => {
                navigate('/profile');
              }}
              style={{
                fontSize: '17px',
                fontFamily: 'Roboto400',
                marginLeft: 4,
                marginRight: 4,
                color: '#C21B17',
                textDecoration: 'underline',
              }}
            >
              {t('common.cancel')}
            </Link>
          </Row>
        </Card>
      </div>
    </ResponsiveContainer>
  );
};
